import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const firstEight = categories.slice(0, 8);
  const remaining = categories.slice(8);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      const response = await fetch('https://readdy.ai/api/form/d8r5rs0n4gr6p3b2gdhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-900 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-12 md:mb-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-file-list-3-line text-white text-base"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold">Litbuy Spreadsheets</span>
          </Link>
          <p className="text-sm text-white/60 max-w-md leading-relaxed">
            Your trusted resource for Litbuy Spreadsheet guides, reviews, and comparisons. Helping e-commerce sellers make smarter data decisions since 2024.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          <div>
            <h4 className="font-heading text-sm font-semibold mb-4 text-white">Categories 1-8</h4>
            <ul className="flex flex-col gap-2.5">
              {firstEight.map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-xs md:text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold mb-4 text-white">Categories 9-14</h4>
            <ul className="flex flex-col gap-2.5">
              {remaining.map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-xs md:text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold mb-4 text-white">Resources</h4>
            <ul className="flex flex-col gap-2.5">
              <li>
                <Link to="/litbuy-spreadsheet-guide" className="text-xs md:text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer">
                  Complete Guide
                </Link>
              </li>
              <li>
                <Link to="/litbuy-spreadsheet-beginners" className="text-xs md:text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer">
                  Beginner Guide
                </Link>
              </li>
              <li>
                <Link to="/litbuy-spreadsheet-features" className="text-xs md:text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer">
                  Features Overview
                </Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-xs md:text-sm text-white/60 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer">
                  Main Store
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1">
            <p className="font-heading text-base font-semibold text-white/80">&copy; 2024-2026 Litbuy Spreadsheets</p>
            <p className="text-xs text-white/50 mt-1 font-heading italic">All rights reserved</p>
            <div className="flex items-center gap-4 mt-4">
              <a href="#" className="text-xs text-white/40 hover:text-primary-400 transition-colors underline cursor-pointer">Twitter</a>
              <a href="#" className="text-xs text-white/40 hover:text-primary-400 transition-colors underline cursor-pointer">LinkedIn</a>
              <a href="#" className="text-xs text-white/40 hover:text-primary-400 transition-colors underline cursor-pointer">YouTube</a>
              <a href="#" className="text-xs text-white/40 hover:text-primary-400 transition-colors underline cursor-pointer">Pinterest</a>
            </div>
          </div>
        </div>

        <div className="mt-12 md:mt-16 pt-10 md:pt-12 border-t border-white/10 flex flex-col lg:flex-row gap-8">
          <div className="lg:w-2/5">
            <div className="rounded-xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Calm%20minimal%20home%20office%20corner%20with%20a%20macbook%20on%20a%20wooden%20desk%2C%20warm%20table%20lamp%20glow%2C%20bookshelf%20with%20organized%20binders%2C%20evening%20atmosphere%2C%20cozy%20and%20productive%20workspace%20interior%20photography&width=800&height=600&seq=footer-v3-office&orientation=landscape"
                alt="Litbuy Spreadsheet office"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:w-1/5 flex items-center">
            <p className="font-heading text-xl md:text-2xl text-white/50 leading-relaxed">
              Litbuy
              <br />
              Spreadsheet
              <br />
              Tools
            </p>
          </div>

          <div className="lg:w-2/5">
            <p className="text-sm text-white/60 mb-3">
              Subscribe to our newsletter for the latest Litbuy Spreadsheet tips and updates.
            </p>
            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="flex flex-col sm:flex-row gap-2"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/40 outline-none focus:border-primary-400 transition-colors"
              />
              <button
                type="submit"
                disabled={submitStatus === 'loading'}
                className="whitespace-nowrap cursor-pointer px-5 py-3 rounded-lg bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 flex items-center gap-2 justify-center"
              >
                {submitStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                <i className="ri-arrow-right-line"></i>
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-xs text-accent-400 mt-2">Thank you! You are now subscribed.</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-xs text-red-400 mt-2">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}